import React, { useState, useEffect } from 'react';
import { 
  Lock, 
  ArrowLeft,
  MessageCircle,
  Play,
  X,
  Sparkles,
  Palette,
  Image as ImageIcon,
  Bell,
  Loader2,
  Tag
} from 'lucide-react';
import { getPublicCatalogueByPublicId, subscribeToCollection } from '../api';
import { getKitsByFabricImagePublic } from '../api/kit.api';
import FabricAnalysisReadOnly from './FabricAnalysisReadOnly';

function resolveFabricImage(fabric) {
  if (!fabric) return null;
  return (
    fabric.image ||
    fabric.images?.original?.s3Url ||
    fabric.images?.original?.imgbbUrl ||
    fabric.images?.original?.url ||
    null
  );
}

function normalizeShadeCards(fabric) {
  if (!fabric) return [];
  const raw = fabric.shadeCards || (fabric.shadeCard ? [fabric.shadeCard] : []);
  return raw
    .map((entry) => {
      if (typeof entry === 'string') return { url: entry };
      if (entry?.url) return entry;
      return null;
    })
    .filter(Boolean);
}

function mergeFabricKits(catalogueKits = [], fetchedKits = []) {
  const byId = new Map();
  catalogueKits.forEach((kit) => {
    if (kit?._id) byId.set(String(kit._id), kit);
  });
  fetchedKits.forEach((kit) => {
    if (kit?._id && !byId.has(String(kit._id))) {
      byId.set(String(kit._id), kit);
    }
  });
  return Array.from(byId.values());
}

const PublicCatalogueView = ({ publicId, showNotification, setActiveView, setProductDetails, onOpenLoginModal }) => {
  const notify = (message, type = 'info') => {
    if (showNotification) {
      showNotification(message, type);
    } else {
      window.alert(message);
    }
  };
  const navigate = () => {
    // Simple navigation function for this app
    window.location.href = '/';
  };
  const [catalogue, setCatalogue] = useState(null);
  const [fabricDetails, setFabricDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [password, setPassword] = useState('');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [fullImageUrl, setFullImageUrl] = useState(null);
  const [fullImageIndex, setFullImageIndex] = useState(-1);
  const [allViewableImages, setAllViewableImages] = useState([]);
  const [brokenImages, setBrokenImages] = useState(new Set());
  const [allGeneratedKits, setAllGeneratedKits] = useState([]);
  const [isLoadingKits, setIsLoadingKits] = useState(false);
  const [fabricKits, setFabricKits] = useState([]);
  const [isLoadingFabricKits, setIsLoadingFabricKits] = useState(false);
  const [isSubscribing, setIsSubscribing] = useState(false);

  useEffect(() => {
    console.log('🎯 PublicCatalogueView mounted with publicId:', publicId);
    if (publicId) {
      loadCatalogue();
    }
  }, [publicId]);

  // Debug: Log when props are received
  useEffect(() => {
    console.log('📋 PublicCatalogueView Props:');
    console.log('  - setActiveView:', typeof setActiveView);
    console.log('  - setProductDetails:', typeof setProductDetails);
    console.log('  - onOpenLoginModal:', typeof onOpenLoginModal);
  }, [setActiveView, setProductDetails, onOpenLoginModal]);

  // Load kits from catalogue (already fetched by public API)
  useEffect(() => {
    // The catalogue already contains all the kits in kitIds
    // No need to make a separate authenticated API call
    if (catalogue && catalogue.kitIds && catalogue.kitIds.length > 0) {
      console.log(`✅ Found ${catalogue.kitIds.length} kits in catalogue`);
      setAllGeneratedKits(catalogue.kitIds);
      setIsLoadingKits(false);
    } else if (catalogue) {
      // Catalogue loaded but no kits
      console.log('ℹ️ Catalogue loaded but no kits found');
      setAllGeneratedKits([]);
      setIsLoadingKits(false);
    }
  }, [catalogue]);

  // Fetch kits for the fabric when fabric details are available
  useEffect(() => {
    const fetchFabricKits = async () => {
      if (!fabricDetails) {
        setFabricKits([]);
        return;
      }

      const fabricImage = fabricDetails.image || 
                          fabricDetails.images?.original?.s3Url || 
                          fabricDetails.images?.original?.imgbbUrl || 
                          fabricDetails.images?.original?.url;

      if (!fabricImage) {
        console.log('ℹ️ No fabric image available to fetch kits');
        setFabricKits([]);
        return;
      }

      setIsLoadingFabricKits(true);
      try {
        console.log('🔍 [PUBLIC] Fetching kits for fabric image:', fabricImage);
        const kits = await getKitsByFabricImagePublic(fabricImage);
        console.log(`✅ [PUBLIC] Found ${kits.length} kits for this fabric`);
        setFabricKits(kits);
      } catch (error) {
        console.error('❌ [PUBLIC] Error fetching fabric kits:', error);
        setFabricKits([]);
      } finally {
        setIsLoadingFabricKits(false);
      }
    };

    fetchFabricKits();
  }, [fabricDetails]);

  const loadCatalogue = async (providedPassword = null) => {
    try {
      console.log('📡 Loading catalogue for publicId:', publicId, 'with password:', providedPassword ? '***' : 'none');
      setLoading(true);
      setError(null);
      setIsLoadingKits(true); // Set loading state for kits
      
      const response = await getPublicCatalogueByPublicId(publicId, providedPassword);
      console.log('✅ Catalogue loaded successfully:', response.data.data);
      console.log('📦 Fabric Details:', response.data.data.fabricDetails);
      console.log('🎨 Shade Cards:', response.data.data.fabricDetails?.shadeCards);
      console.log('🌈 Color Variants:', response.data.data.fabricDetails?.colorVariants);
      console.log('🎯 Kits count:', response.data.data.catalogue?.kitIds?.length || 0);
      setCatalogue(response.data.data.catalogue);
      setFabricDetails(response.data.data.fabricDetails || null);
    } catch (err) {
      console.error('❌ Error loading catalogue:', err);
      if (err.response?.status === 401 && err.response?.data?.requiresPassword) {
        console.log('🔒 Password required for catalogue');
        setShowPasswordModal(true);
      } else {
        console.log('💥 Catalogue load failed:', err.response?.data?.message || err.message);
        setError(err.response?.data?.message || 'Failed to load catalogue');
      }
      setIsLoadingKits(false); // Reset loading state on error
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordError('');
    
    try {
      await loadCatalogue(password);
      setShowPasswordModal(false);
      setPassword('');
    } catch (err) {
      setPasswordError('Invalid password');
    }
  };

  const handleImageError = (imageUrl) => {
    console.warn('Failed to load image:', imageUrl);
    setBrokenImages(prev => new Set([...prev, imageUrl]));
  };

  const isImageBroken = (imageUrl) => brokenImages.has(imageUrl);

  // Build list of all viewable images (fabric image + shade cards)
  useEffect(() => {
    if (!fabricDetails) {
      setAllViewableImages([]);
      return;
    }

    const images = [];
    
    // Add main fabric image
    const fabricImage = fabricDetails.image || 
                        fabricDetails.images?.original?.s3Url || 
                        fabricDetails.images?.original?.imgbbUrl || 
                        fabricDetails.images?.original?.url;
    if (fabricImage && !brokenImages.has(fabricImage)) {
      images.push({ url: fabricImage, type: 'fabric', index: 0 });
    }
    
    // Add shade cards
    const shadeCards = normalizeShadeCards(fabricDetails);
    shadeCards.forEach((shadeCard, idx) => {
      const shadeCardUrl = shadeCard.url;
      if (shadeCardUrl && !brokenImages.has(shadeCardUrl)) {
        images.push({ url: shadeCardUrl, type: 'shadeCard', index: idx + 1 });
      }
    });
    
    setAllViewableImages(images);
  }, [fabricDetails, brokenImages]);

  const handleImageClick = (imageUrl) => {
    if (!isImageBroken(imageUrl)) {
      const imageIndex = allViewableImages.findIndex(img => img.url === imageUrl);
      if (imageIndex !== -1) {
        setFullImageIndex(imageIndex);
        setFullImageUrl(imageUrl);
      } else {
        setFullImageUrl(imageUrl);
        setFullImageIndex(-1);
      }
    }
  };

  const handleNextImage = () => {
    if (fullImageIndex >= 0 && fullImageIndex < allViewableImages.length - 1) {
      const nextIndex = fullImageIndex + 1;
      const nextImage = allViewableImages[nextIndex];
      setFullImageIndex(nextIndex);
      setFullImageUrl(nextImage.url);
    }
  };

  const handlePreviousImage = () => {
    if (fullImageIndex > 0) {
      const prevIndex = fullImageIndex - 1;
      const prevImage = allViewableImages[prevIndex];
      setFullImageIndex(prevIndex);
      setFullImageUrl(prevImage.url);
    }
  };

  const handleCloseImage = () => {
    setFullImageUrl(null);
    setFullImageIndex(-1);
  };

  // Keyboard navigation for image viewer
  useEffect(() => {
    if (!fullImageUrl) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        handleCloseImage();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        handleNextImage();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        handlePreviousImage();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [fullImageUrl, fullImageIndex, allViewableImages]);

  const downloadKitImage = (kit, imageUrl, filename) => {
    if (!imageUrl) return;
    
    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleGenerateDesigns = () => {
    // Check if user is logged in
    const token = localStorage.getItem('token');
    
    console.log('🎨 Generate Kit with this Fabric - Button Clicked');
    console.log('📊 User Authentication Status:', !!token ? 'LOGGED IN' : 'NOT LOGGED IN');
    console.log('📌 onOpenLoginModal available:', !!onOpenLoginModal);
    
    if (!token) {
      // User not logged in - store fabric and show login/signup modal (same as landing page)
      console.log('🔓 User not authenticated - Opening Login/Signup Modal');
      
      if (fabricDetails) {
        // Save fabric details to localStorage for auto-selection after login
        const fabricImage = fabricDetails.image || fabricDetails.images?.original?.s3Url || fabricDetails.images?.original?.imgbbUrl || fabricDetails.images?.original?.url;
        const fabricData = {
          name: fabricDetails.name || '',
          description: fabricDetails.description || '',
          image: fabricImage,
          targetAudience: fabricDetails.targetAudience || '',
          category: fabricDetails.category || '',
          type: fabricDetails.productType || fabricDetails.type || ''
        };
        localStorage.setItem('selectedFabricForGeneration', JSON.stringify(fabricData));
        console.log('✅ Fabric details stored in localStorage for auto-selection after login');
      }
      
      if (onOpenLoginModal && typeof onOpenLoginModal === 'function') {
        console.log('✅ Triggering Login/Signup Modal');
        onOpenLoginModal();
        // Important: Return here to prevent further execution
        return;
      } else {
        console.error('❌ ERROR: onOpenLoginModal function is not available or not a function');
        console.error('Falling back to manual redirect...');
        // Fallback: redirect to login page
        if (setActiveView && typeof setActiveView === 'function') {
          setActiveView('login');
        }
        return;
      }
    } else {
      // User is logged in - save fabric and navigate to root
      console.log('✅ User logged in, saving fabric and navigating to Generate');
      
      if (fabricDetails) {
        // Save fabric details to localStorage for auto-selection
        const fabricImage = fabricDetails.image || fabricDetails.images?.original?.s3Url || fabricDetails.images?.original?.imgbbUrl || fabricDetails.images?.original?.url;
        const fabricData = {
          name: fabricDetails.name || '',
          description: fabricDetails.description || '',
          image: fabricImage,
          targetAudience: fabricDetails.targetAudience || '',
          category: fabricDetails.category || '',
          type: fabricDetails.productType || fabricDetails.type || ''
        };
        
        localStorage.setItem('selectedFabricForGeneration', JSON.stringify(fabricData));
        console.log('✅ Fabric saved to localStorage:', fabricData);
      }
      
      // Navigate to root - app will detect logged in user and show generate kit page
      console.log('🚀 Navigating to root page /');
      window.location.href = '/';
    }
  };

  const shareCatalogue = async () => {
    const url = window.location.href;
    
    try {
      if (navigator.share) {
        await navigator.share({
          title: catalogue.title,
          text: catalogue.description || 'Check out this marketing catalogue',
          url: url
        });
        // Share was successful
        console.log('✅ Share successful');
        if (showNotification) {
          showNotification('Catalogue shared successfully!', 'success');
        }
      } else {
        // Fallback to clipboard
        await navigator.clipboard.writeText(url);
        if (showNotification) {
          showNotification('Link copied to clipboard!', 'success');
        } else {
          alert('Link copied to clipboard!');
        }
      }
    } catch (error) {
      // Handle share cancellation or other errors
      if (error.name === 'AbortError') {
        // User cancelled the share dialog - this is normal, don't show error
        console.log('ℹ️ Share cancelled by user');
        return;
      } else {
        // Other error occurred, fallback to clipboard
        console.error('❌ Share failed, falling back to clipboard:', error);
        try {
          await navigator.clipboard.writeText(url);
          if (showNotification) {
            showNotification('Link copied to clipboard!', 'success');
          } else {
            alert('Link copied to clipboard!');
          }
        } catch (clipboardError) {
          console.error('❌ Clipboard also failed:', clipboardError);
          // Final fallback - show the URL for manual copying
          if (showNotification) {
            showNotification('Share failed. Please copy the link manually.', 'error');
          } else {
            alert(`Share failed. Please copy this link manually:\n\n${url}`);
          }
        }
      }
    }
  };

  const handleSubscribe = async () => {
    // Extract phone number from catalogue or user info
    const phoneNumber = catalogue?.user?.phone || catalogue?.phoneNumber || null;
    
    if (!phoneNumber) {
      notify('Unable to find collection owner information', 'error');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      // User is not logged in - store subscription info and open login modal
      const pendingSubscription = {
        phoneNumber: phoneNumber.replace(/\D/g, ''), // Normalize phone number
        source: 'public-catalogue',
        publicId: publicId
      };
      localStorage.setItem('pendingSubscription', JSON.stringify(pendingSubscription));
      console.log('📝 Stored pending subscription:', pendingSubscription);
      
      // Open login modal
      if (onOpenLoginModal && typeof onOpenLoginModal === 'function') {
        onOpenLoginModal();
      } else {
        notify('Please login to subscribe. Your subscription will be completed after login.', 'info');
        if (setActiveView && typeof setActiveView === 'function') {
          setActiveView('login');
        }
      }
      return;
    }

    // User is logged in - directly subscribe
    setIsSubscribing(true);
    try {
      await subscribeToCollection(phoneNumber.replace(/\D/g, ''), token);
      
      notify('Successfully subscribed! You will receive updates on new collections.', 'success');
    } catch (error) {
      console.error('Subscription error:', error);
      if (error.response?.status === 401) {
        // Token expired - store pending subscription and open login modal
        const pendingSubscription = {
          phoneNumber: phoneNumber.replace(/\D/g, ''),
          source: 'public-catalogue',
          publicId: publicId
        };
        localStorage.setItem('pendingSubscription', JSON.stringify(pendingSubscription));
        
        if (onOpenLoginModal && typeof onOpenLoginModal === 'function') {
          onOpenLoginModal();
        }
      }

      const backendMessage = error.response?.data?.message || error.message;
      if (backendMessage?.toLowerCase().includes('already subscribed')) {
        notify('Seller already subscribed.', 'info');
      } else {
        notify(backendMessage || 'Failed to subscribe. Please try again later.', 'error');
      }
    } finally {
      setIsSubscribing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading catalogue...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="bg-red-100 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <X className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Catalogue Not Found</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  if (!catalogue) return null;

  const displayFabric = fabricDetails || (() => {
    const firstKit = (catalogue.kitIds || [])[0];
    const pd = firstKit?.productDetails;
    if (!pd) return null;
    return {
      name: pd.name,
      description: pd.description,
      image: pd.image || pd.originalImage,
      tags: [],
      properties: [],
      fabricAnalysis: null,
      fabricWeight: null,
      shadeCards: [],
      colorVariants: pd.colorVariants || [],
    };
  })();

  const fabricImage = resolveFabricImage(displayFabric);
  const shadeCards = normalizeShadeCards(displayFabric);
  const colorVariants = displayFabric?.colorVariants || [];
  const allFabricKits = mergeFabricKits(catalogue?.kitIds || allGeneratedKits, fabricKits);
  const kitsLoading = isLoadingKits || isLoadingFabricKits;

  const renderKitCard = (kit) => {
    const kitImage = kit.generatedImageUrl || kit.productDetails?.image;
    if (!kitImage) return null;
    return (
      <div
        key={kit._id}
        className="group bg-white rounded-xl border-2 border-gray-200 hover:border-purple-400 hover:shadow-xl transition-all duration-300 overflow-hidden cursor-pointer active:scale-[0.99]"
        onClick={() => handleImageClick(kitImage)}
      >
        <div className="relative aspect-[3/4] bg-gradient-to-br from-gray-50 to-gray-100">
          <img
            src={kitImage}
            alt={kit.name || 'Generated concept'}
            className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
            onError={(e) => {
              e.target.src = 'https://placehold.co/400x400/ffe4f2/ede9fe?text=No+Image';
            }}
          />
          {kit.status === 'generated' && (
            <div className="absolute top-2 right-2 bg-green-500 text-white px-2 py-1 rounded-full text-xs font-semibold shadow-lg">
              ✓ Ready
            </div>
          )}
          {kit.videoUrl && (
            <div className="absolute top-2 left-2 bg-purple-500 text-white px-2 py-1 rounded-full text-xs font-semibold flex items-center gap-1 shadow-lg">
              <Play size={12} />
              Video
            </div>
          )}
        </div>
        <div className="p-3 bg-gradient-to-b from-white to-purple-50/30">
          <h4 className="font-semibold text-gray-800 text-sm truncate">{kit.name || 'Generated concept'}</h4>
          <p className="text-xs text-gray-500 mt-1">
            {new Date(kit.createdAt || kit.dateGenerated).toLocaleDateString()}
          </p>
          {kit.generatedColorVariants && kit.generatedColorVariants.length > 0 && (
            <div className="mt-2 flex gap-1">
              {kit.generatedColorVariants.slice(0, 4).map((variant, vIndex) => (
                <div
                  key={vIndex}
                  className="w-6 h-6 rounded border-2 border-gray-300"
                  style={{ backgroundColor: variant.hex }}
                  title={variant.colorName || variant.hex}
                />
              ))}
              {kit.generatedColorVariants.length > 4 && (
                <div className="w-6 h-6 rounded border-2 border-gray-300 bg-gray-100 flex items-center justify-center text-xs font-semibold text-gray-600">
                  +{kit.generatedColorVariants.length - 4}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Minimal top bar for mobile: back + share */}
      <div className="sticky top-0 z-10 bg-white/90 backdrop-blur border-b">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-2 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Back"
          >
            <ArrowLeft size={18} />
          </button>
          <div className="flex-1 px-2 truncate text-center text-sm font-semibold text-gray-800">
            {catalogue.title}
          </div>
          <div className="flex items-center gap-2">
          <button
            onClick={handleSubscribe}
            disabled={isSubscribing}
            className="px-3 py-1.5 text-xs sm:text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 active:scale-95 transition flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Subscribe"
          >
            {isSubscribing ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                <span className="hidden sm:inline">Subscribing...</span>
              </>
            ) : (
              <>
                <Bell size={14} />
                <span className="hidden sm:inline">Subscribe</span>
              </>
            )}
          </button>
            <button
              onClick={shareCatalogue}
              className="px-3 py-1.5 text-xs sm:text-sm bg-purple-500 text-white rounded-lg hover:bg-purple-600 active:scale-95 transition"
            >
              Share
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-6 space-y-6">

        {catalogue.description && (
          <p className="text-sm sm:text-base text-gray-600 text-center max-w-3xl mx-auto">
            {catalogue.description}
          </p>
        )}

        {/* 1. Fabric image + properties */}
        {displayFabric && (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-lg overflow-hidden">
            <div className="grid grid-cols-1 md:grid-cols-[minmax(0,280px)_1fr] lg:grid-cols-[minmax(0,320px)_1fr] gap-5 md:gap-6 p-5 sm:p-6">
              <div className="flex items-start justify-center md:justify-start">
                <div className="relative w-full md:w-[280px] lg:w-[320px] aspect-square rounded-xl overflow-hidden bg-gradient-to-br from-purple-50 to-blue-50 shadow-sm ring-1 ring-gray-100 shrink-0">
                  {fabricImage && !isImageBroken(fabricImage) ? (
                    <img
                      src={fabricImage}
                      alt={displayFabric.name || 'Fabric'}
                      className="w-full h-full object-cover cursor-pointer hover:scale-[1.02] transition-transform duration-300"
                      onClick={() => handleImageClick(fabricImage)}
                      onError={() => handleImageError(fabricImage)}
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-gray-400">
                      <ImageIcon size={48} />
                    </div>
                  )}
                </div>
              </div>

              <div className="flex flex-col justify-center min-w-0">
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                  {displayFabric.name || 'Fabric'}
                </h2>
                {displayFabric.description && (
                  <p className="text-gray-600 mb-4 text-sm sm:text-base">{displayFabric.description}</p>
                )}

                {displayFabric.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {displayFabric.tags.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-medium"
                      >
                        <Tag size={12} />
                        {tag}
                      </span>
                    ))}
                  </div>
                )}

                {displayFabric.properties?.length > 0 && (
                  <div className="mb-4 rounded-xl border border-gray-200 bg-gray-50/80 p-4">
                    <p className="text-sm font-semibold text-gray-800 mb-3">Properties</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                      {displayFabric.properties.map((prop, index) => (
                        <div key={`${prop.key}-${index}`} className="text-sm">
                          <span className="font-medium text-gray-700">{prop.key}:</span>{' '}
                          <span className="text-gray-600">{prop.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <FabricAnalysisReadOnly
                  fabricAnalysis={displayFabric.fabricAnalysis}
                  fabricWeight={displayFabric.fabricWeight}
                />

                <button
                  type="button"
                  onClick={handleGenerateDesigns}
                  className="mt-4 w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:from-purple-600 hover:to-pink-600 transition-all font-semibold flex items-center justify-center gap-2 shadow-lg hover:shadow-xl active:scale-[0.98]"
                >
                  <Sparkles size={20} />
                  Generate with this fabric
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 2. Shade cards + extracted colors */}
        {displayFabric && (shadeCards.length > 0 || colorVariants.length > 0) && (
          <div className="bg-white rounded-2xl border-2 border-purple-200 shadow-lg p-5 sm:p-6 space-y-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center shrink-0">
                <ImageIcon size={20} className="text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-800">Shade card & colors</h3>
                <p className="text-sm text-gray-600">
                  {shadeCards.length > 0
                    ? `${shadeCards.length} shade card${shadeCards.length > 1 ? 's' : ''}`
                    : 'Extracted color palette'}
                  {colorVariants.length > 0
                    ? ` · ${colorVariants.length} color${colorVariants.length > 1 ? 's' : ''} extracted`
                    : ''}
                </p>
              </div>
            </div>

            {shadeCards.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {shadeCards.map((shadeCard, index) => {
                  const shadeCardUrl = shadeCard.url;
                  if (!shadeCardUrl || isImageBroken(shadeCardUrl)) return null;
                  return (
                    <div
                      key={shadeCardUrl}
                      className="group relative bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl overflow-hidden border-2 border-purple-200 hover:border-purple-400 hover:shadow-xl transition-all cursor-pointer"
                      onClick={() => handleImageClick(shadeCardUrl)}
                    >
                      <div className="relative aspect-video min-h-[140px]">
                        <img
                          src={shadeCardUrl}
                          alt={`Shade card ${index + 1}`}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          onError={() => handleImageError(shadeCardUrl)}
                        />
                        <div className="absolute top-2 left-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg pointer-events-none">
                          Shade card #{index + 1}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {colorVariants.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Palette size={18} className="text-purple-600" />
                  <h4 className="text-base font-semibold text-gray-800">Extracted colors</h4>
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3 sm:gap-4">
                  {colorVariants.map((variant, index) => {
                    const colorName = variant.colorName || variant.name || `Color ${index + 1}`;
                    const hex = variant.hex || '#000000';
                    return (
                      <div
                        key={`${hex}-${index}`}
                        className="rounded-xl border-2 border-gray-200 overflow-hidden hover:border-purple-400 transition-colors"
                      >
                        <div className="aspect-square" style={{ backgroundColor: hex }} title={`${colorName} - ${hex}`} />
                        <div className="p-2 bg-white">
                          <p className="text-xs font-semibold text-gray-800 text-center truncate" title={colorName}>
                            {colorName}
                          </p>
                          <p className="text-[10px] font-mono text-gray-500 text-center truncate" title={hex}>
                            {hex}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* 3. All kits generated from this fabric */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-lg p-5 sm:p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center shrink-0">
              <Sparkles size={20} className="text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-800">Generated concepts</h3>
              <p className="text-sm text-gray-600">
                {kitsLoading ? (
                  <span className="text-purple-500">Loading kits…</span>
                ) : (
                  `${allFabricKits.length} kit${allFabricKits.length !== 1 ? 's' : ''} from this fabric`
                )}
              </p>
            </div>
          </div>

          {kitsLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500" />
            </div>
          ) : allFabricKits.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {allFabricKits.map(renderKitCard)}
            </div>
          ) : (
            <div className="text-center py-10 text-gray-500">
              <Sparkles size={32} className="mx-auto mb-3 text-gray-300" />
              <p>No generated concepts for this fabric yet.</p>
              {displayFabric && (
                <button
                  type="button"
                  onClick={handleGenerateDesigns}
                  className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-semibold"
                >
                  <Sparkles size={16} />
                  Generate first concept
                </button>
              )}
            </div>
          )}
        </div>

      </div>

      {/* Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="bg-purple-100 rounded-full p-2">
                  <Lock size={20} className="text-purple-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">Password Required</h3>
                  <p className="text-sm text-gray-600">This catalogue is password protected</p>
                </div>
              </div>
              
              <form onSubmit={handlePasswordSubmit}>
                <div className="mb-4">
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter password"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    required
                  />
                  {passwordError && (
                    <p className="text-red-500 text-sm mt-2">{passwordError}</p>
                  )}
                </div>
                
                <div className="flex items-center space-x-3">
                  <button
                    type="submit"
                    className="flex-1 px-4 py-3 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors"
                  >
                    Access Catalogue
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/')}
                    className="px-4 py-3 text-gray-600 hover:text-gray-800 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}


      {/* Fullscreen Image Viewer */}
      {fullImageUrl && !isImageBroken(fullImageUrl) && (
        <div className="fixed inset-0 z-50 bg-black/95 backdrop-blur-sm flex items-center justify-center p-4" onClick={handleCloseImage}>
          {/* Close Button */}
          <button
            className="absolute top-4 right-4 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors backdrop-blur-sm z-10"
            onClick={handleCloseImage}
            aria-label="Close"
          >
            <X size={24} />
          </button>

          {/* Navigation Buttons */}
          {allViewableImages.length > 1 && fullImageIndex >= 0 && (
            <>
              {fullImageIndex > 0 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePreviousImage();
                  }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors backdrop-blur-sm z-10"
                  aria-label="Previous image"
                >
                  <ArrowLeft size={24} />
                </button>
              )}
              {fullImageIndex < allViewableImages.length - 1 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleNextImage();
                  }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors backdrop-blur-sm z-10"
                  aria-label="Next image"
                >
                  <ArrowLeft size={24} className="rotate-180" />
                </button>
              )}
              {/* Image Counter */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white text-sm font-medium z-10">
                {fullImageIndex + 1} / {allViewableImages.length}
              </div>
            </>
          )}

          {/* Image */}
          <img 
            src={fullImageUrl} 
            alt="Full view" 
            className="max-h-full max-w-full object-contain rounded-xl cursor-zoom-out"
            onClick={(e) => e.stopPropagation()}
            onError={() => {
              handleImageError(fullImageUrl);
              handleCloseImage();
            }}
          />

          {/* Instructions */}
          {allViewableImages.length > 1 && (
            <div className="absolute top-20 left-1/2 -translate-x-1/2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-lg text-white text-xs text-center z-10">
              Use arrow keys or buttons to navigate • Press ESC to close
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PublicCatalogueView;
